/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Logger } from '@app/core';
import { ContactComponent } from '@app/shared/components/contact/contact.component';
import { ContactPerson } from '@app/shared/components/contact-person/contact-person.model';

const log = new Logger('appContactPersonComponent');

@Component({
  selector: 'app-contact-person',
  templateUrl: './contact-person.component.html',
  styleUrls: ['./contact-person.component.scss'],
})
export class ContactPersonComponent implements OnInit {
  @Input() parentFormGroup: FormGroup;
  @Input() withEvent = false;
  @Input() cols = 12;
  @Input() required = false;
  @Input() showName = false;
  @Input() showNameFrom = 'name';
  @Input() showNote = false;

  /**
   * Правя го static, за да може да се вика и от други компоненти, понеже се налага...
   * @return FormGroup
   */
  public static create(contactsCount: number = 1, withEvent: boolean = false, required: boolean = false): FormGroup {
    const contactsFormArray = new FormArray([]);

    for (let i = 1; i <= contactsCount; i++) {
      contactsFormArray.push(ContactComponent.getEmptyContact(required));
    }

    let requiredRules: Validators = [];

    if (required) {
      requiredRules = [Validators.required];
    }

    const controls = {
      id: new FormControl(null),
      title_id: new FormControl(null, requiredRules),
      position: new FormControl(null),
      first_name: new FormControl('', requiredRules),
      last_name: new FormControl('', requiredRules),
      contacts: contactsFormArray,
    };

    if (withEvent) {
      controls['show'] = new FormControl(false);
      controls['event_group_id'] = new FormControl(null);
    }
    return new FormGroup(controls);
  }

  /**
   * Създава много контакти от модел ...
   * @param contactPersons any
   * @param withEvent дали да има Event
   * @param required Дали са задължителни полетата
   */
  public static createMany(
    contactPersons: Array<ContactPerson>,
    withEvent: boolean = false,
    required: boolean = false
  ): FormArray {
    const formArray: FormArray = new FormArray([]);
    contactPersons.forEach((contactPerson: ContactPerson) => {
      formArray.push(ContactPersonComponent.create(contactPerson.contacts.length, withEvent, required));
    });
    return formArray;
  }

  constructor(private http: HttpClient) {}

  /**
   * Check all contacts for all contact persons. Sets per-contact `duplicate`, `duplicateMessage`, `duplicateChecked` flags.
   * Returns Observable<boolean> which emits true if any duplicate was found.
   */
  public checkAllContactsExists(): Observable<boolean> {
    const cpsArray = this.parentFormGroup.get('contact_persons') as FormArray;
    if (!cpsArray || cpsArray.length <= 0) {
      return of(false);
    }

    const checks: Observable<boolean>[] = [];

    for (let i = 0; i < cpsArray.length; i++) {
      const cpGroup = cpsArray.at(i) as FormGroup;
      const contactsArray = cpGroup.get('contacts') as FormArray;
      if (!contactsArray || contactsArray.length <= 0) continue;

      for (let j = 0; j < contactsArray.length; j++) {
        const contactGroup = contactsArray.at(j) as FormGroup;
        const typeId = contactGroup.get('type_id')?.value;
        const valueControl = contactGroup.get('value');
        const value = valueControl?.value;
        const contactId = contactGroup.get('id')?.value;

        const dupCheckedControl = contactGroup.get('duplicateChecked');
        const dupControl = contactGroup.get('duplicate');
        const dupMsgControl = contactGroup.get('duplicateMessage');

        if (dupCheckedControl?.value) {
          checks.push(of(!!dupControl?.value));
          continue;
        }

        if (!typeId || !value || valueControl?.errors?.invalidPhone) {
          if (dupControl) dupControl.setValue(false, { emitEvent: false });
          if (dupMsgControl) dupMsgControl.setValue(null, { emitEvent: false });
          if (dupCheckedControl) dupCheckedControl.setValue(true, { emitEvent: false });
          checks.push(of(false));
          continue;
        }

        const obs = this.http
          .get('v1/contacts/check-exists', {
            params: {
              type_id: typeId,
              value: value,
              ...(contactId ? { contact_id: contactId } : {}),
            },
          })
          .pipe(
            map(() => {
              if (dupControl) dupControl.setValue(false, { emitEvent: false });
              if (dupMsgControl) dupMsgControl.setValue(null, { emitEvent: false });
              if (dupCheckedControl) dupCheckedControl.setValue(true, { emitEvent: false });
              return false;
            }),
            catchError((err) => {
              const dup = err && err.status === 409;
              if (dupControl) dupControl.setValue(!!dup, { emitEvent: false });
              const serverMsg = (err && err.error && (err.error.message || err.error.msg)) || err.message || null;
              if (dupMsgControl) dupMsgControl.setValue(serverMsg, { emitEvent: false });
              if (dupCheckedControl) dupCheckedControl.setValue(true, { emitEvent: false });
              return of(!!dup);
            })
          );

        checks.push(obs);
      }
    }

    if (checks.length === 0) return of(false);
    return forkJoin(checks).pipe(map((results) => results.some((r) => !!r)));
  }

  ngOnInit() {
    /*
     * Проверявам дали има contact person-и ако няма значи компонента е извикан от add бутон
     * При това положение добавям един contact person.
     *
     * Ако има значи компонента е извикан от edit бутон и няма нужда да се добавя празен contact person
     * понеже формата ще се напълни с вече съществуващи такива
     *
     * Без timeout contactPersons.length винаги е 0
     */
    setTimeout(() => {
      // @ts-ignore
      if (this.parentFormGroup.get('contact_persons').length <= 0 && this.required) {
        this.addContactPerson();
      }
    });
  }

  addContactPerson() {
    log.debug('addContactPerson');
    // @ts-ignore
    this.parentFormGroup.get('contact_persons').push(ContactPersonComponent.create(1, this.withEvent, this.required));

    this.arrayMove(
      this.parentFormGroup.get('contact_persons')['controls'],
      this.parentFormGroup.get('contact_persons')['controls'].length - 1,
      0
    );
  }

  removeContactPerson(index: number) {
    this.parentFormGroup.get('contact_persons')['controls'].splice(index, 1);
    this.parentFormGroup.get('contact_persons').updateValueAndValidity();
  }

  moveDown(index: number) {
    this.arrayMove(this.parentFormGroup.get('contact_persons')['controls'], index, index + 1);
    this.parentFormGroup.get('contact_persons').updateValueAndValidity();
  }

  moveUp(index: number) {
    this.arrayMove(this.parentFormGroup.get('contact_persons')['controls'], index, index - 1);
    this.parentFormGroup.get('contact_persons').updateValueAndValidity();
  }

  arrayMove(arr: Array<any>, old_index: number, new_index: number) {
    while (old_index < 0) {
      old_index += arr.length;
    }
    while (new_index < 0) {
      new_index += arr.length;
    }
    if (new_index >= arr.length) {
      let k = new_index - arr.length + 1;
      while (k--) {
        arr.push(undefined);
      }
    }
    arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
    return arr; // for testing purposes
  }
}
